import { useCallback, useEffect, useRef } from 'react';
import { useConversationStore } from '@/store/conversation';
import {
  createConversation,
  saveUserMessage,
  retrieveContext,
  streamChatResponse,
  generateSessionId,
  handleKnowledgeGap,
} from '@/services/conversation';
import type { ConversationContext } from '@/services/conversation';
import type { Citation } from '@/store/conversation';
import type { GraphPathNode } from '@/services/ask-sanjoy/prompts';
import { getEntityRelationships, resolveEntity } from '@/services/relationships';
import type { EntityType } from '@/services/relationships/types';

const SUGGESTED_STARTERS = [
  'How can AI help reduce operational costs in my industry?',
  'What are the most common mistakes in enterprise AI adoption?',
  'I want to improve customer experience with automation.',
  'Help me build an AI implementation roadmap for my business.',
];

function extractCitations(content: string): Citation[] {
  const citations: Citation[] = [];
  const linkPattern = /\*\*\[([^\]]+)\]\(\/([^/]+)\/([^)]+)\)\*\*/g;
  let match;

  while ((match = linkPattern.exec(content)) !== null) {
    citations.push({
      title: match[1] || '',
      entityType: match[2] || '',
      slug: match[3] || '',
    });
  }

  return citations;
}

function extractSuggestedQuestions(content: string): string[] {
  const lines = content.split('\n');
  const questions: string[] = [];

  let inQuestionSection = false;
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.includes('follow-up') || trimmed.includes('explore next') || trimmed.includes('consider asking')) {
      inQuestionSection = true;
      continue;
    }
    if (inQuestionSection && trimmed.startsWith('-')) {
      const q = trimmed.replace(/^-\s*/, '').replace(/\*\*/g, '').trim();
      if (q.endsWith('?') && q.length > 10) {
        questions.push(q);
      }
    }
  }

  if (questions.length === 0) {
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.endsWith('?') && trimmed.length > 15 && !trimmed.startsWith('#')) {
        questions.push(trimmed.replace(/^[-*]\s*/, '').replace(/\*\*/g, ''));
      }
    }
  }

  return questions.slice(0, 3);
}

export function useAskSanjoy(initialContext?: ConversationContext) {
  const store = useConversationStore();
  const sessionIdRef = useRef<string>('');
  const initializedRef = useRef(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionIdRef.current = generateSessionId();
    }
  }, []);

  useEffect(() => {
    if (initialContext && !initializedRef.current) {
      store.setContext(initialContext);
      initializedRef.current = true;
    }
  }, [initialContext]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || store.isStreaming) return;

      store.setError(null);

      // Add user message to store
      const userMessage = {
        id: crypto.randomUUID(),
        role: 'user' as const,
        content: trimmed,
        timestamp: new Date().toISOString(),
      };
      store.addMessage(userMessage);

      // Ensure conversation exists
      let convId = store.conversationId;
      if (!convId) {
        convId = await createConversation(
          store.context,
          sessionIdRef.current
        );
        if (convId) {
          store.setConversationId(convId);
        }
      }

      // Save user message to DB
      if (convId) {
        await saveUserMessage(convId, trimmed);
      }

      // Retrieve knowledge context
      const retrieved = await retrieveContext(trimmed, store.context);

      // Build graph paths from retrieved chunks
      const graphPaths: GraphPathNode[] = [];
      const topChunk = retrieved.chunks[0];
      if (topChunk) {
        try {
          const entityType = topChunk.entityType as EntityType;
          const resolved = await resolveEntity(entityType, topChunk.slug);
          if (resolved) {
            const rels = await getEntityRelationships(resolved.type, resolved.id, {
              limit: 5,
            });
            for (const rel of rels) {
              graphPaths.push({
                entityType: rel.target_entity_type,
                entityId: rel.target_entity_id,
                title: (rel.metadata?.title as string) || rel.target_entity_type,
                relationship: rel.relationship_name,
                depth: 1,
              });
            }
          }
        } catch {
          // Graph context is best-effort
        }
      }

      // Start streaming
      store.startStreaming();

      // Build messages for API
      const apiMessages = store.messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      await streamChatResponse(
        apiMessages,
        store.context,
        retrieved.chunks,
        convId,
        sessionIdRef.current,
        {
          onToken: (token) => {
            store.appendChunk(token);
          },
          onDone: (fullContent) => {
            store.endStreaming(fullContent);

            const citations = extractCitations(fullContent);
            store.setCitations(citations);

            const questions = extractSuggestedQuestions(fullContent);
            store.setSuggestedQuestions(
              questions.length > 0 ? questions : SUGGESTED_STARTERS.slice(0, 2)
            );

            // Knowledge gap detection
            if (retrieved.chunks.length === 0) {
              handleKnowledgeGap(trimmed);
            }
          },
          onError: (error) => {
            store.setError(error);
          },
        },
        graphPaths
      );
    },
    [store]
  );

  const startNewConversation = useCallback(() => {
    store.reset();
    initializedRef.current = false;
  }, [store]);

  return {
    messages: store.messages,
    isStreaming: store.isStreaming,
    streamingContent: store.streamingContent,
    suggestedQuestions:
      store.suggestedQuestions.length > 0
        ? store.suggestedQuestions
        : SUGGESTED_STARTERS,
    citations: store.citations,
    error: store.error,
    conversationId: store.conversationId,
    sendMessage,
    startNewConversation,
  };
}
